import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../../common";
import CheckCircle from "../../common/Icons/CheckCircle";
import EditField from "../../common/Icons/EditField";
import {
	EditableFieldInput,
	type EditableFieldInputProps,
} from "./EditableField.components";
import * as styles from "./EditableField.module.scss";

export type NDSEditableFieldProps = {
	updateFieldIsBeingEdited: (value: boolean) => void;
} & EditableFieldInputProps;
export const NDSEditableField: React.FC<NDSEditableFieldProps> = ({
	updateFieldIsBeingEdited,
	...props
}) => {
	const { value, name, disabled, label, onChange } = props;

	const [isEditingField, setIsEditingField] = useState(false);
	const [displayValue, setDisplayValue] = useState<string | number>(value);

	const handleClick = useCallback(() => {
		setIsEditingField((isEditingFieldPrev) => {
			const isEditable = !isEditingFieldPrev;

			if (isEditable) {
				const fieldToFocus = document.querySelector(`[name="${name}"]`) as
					| HTMLInputElement
					| HTMLButtonElement;
				if (!fieldToFocus) {
					console.warn(`No field found with name: ${name}`);
					return;
				}
				fieldToFocus.disabled = false;
				fieldToFocus.focus();
			}
			return isEditable;
		});
		// biome-ignore lint/suspicious/noExplicitAny: Quick and dirty way of casting as the type depends on union
		onChange(displayValue as any as never);
	}, [displayValue, onChange, name]);

	useEffect(() => {
		updateFieldIsBeingEdited(isEditingField);
	}, [isEditingField, updateFieldIsBeingEdited]);

	const iconSize = "27px";
	const FieldIcon = !isEditingField ? EditField : CheckCircle;
	return (
		<div className={styles.wrapper}>
			<EditableFieldInput
				{...props}
				// biome-ignore lint/suspicious/noExplicitAny: Quick and dirty way of casting as the type depends on union
				value={disabled ? value : (displayValue as any as never)}
				onChange={setDisplayValue}
				disabled={!isEditingField}
			/>
			<Button
				onClick={handleClick}
				onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
					if (event.key === "Enter" || event.key === " ") {
						event.preventDefault();
						handleClick();
					}
				}}
				className={styles.editButton}
				ariaExpanded={!isEditingField}
				ariaLabel={`${isEditingField ? "Save" : "Edit"} '${label}' field`}
			>
				<FieldIcon width={iconSize} height={iconSize} />
			</Button>
		</div>
	);
};
